import { RequestAction } from '@redux-requests/core';
import { createAction as createSmartAction } from 'redux-smart-actions';
import { Group } from 'types';
import { fetchDifferences } from '../differences/actions';
import { Difference, FetchDifferencesData } from '../differences/types';
import { ApiFetchGroups, ApiPatchGroup, FetchGroupsData } from './types';

type FetchGroupsPayload = [
  string, // inspection id
];
export const fetchGroups = createSmartAction<RequestAction<ApiFetchGroups, FetchGroupsData>, FetchGroupsPayload>(
  'differenceGroups/fetchGroups',
  (inspectionId) => ({
    request: {
      url: `/inspections/${inspectionId}/difference-groups`,
      method: 'GET',
    },
    meta: {
      errorMessage: 'There was an error loading the groups. Please try again',
      getData: (data: Group[]) => {
        return data.reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {}); // flatten groups
      },
    },
  }),
);

type PatchGroup = Partial<Pick<Group, 'viewed' | 'comment' | 'smartDiscard' | 'statusId'>>;
type PatchGroupPayload = [
  string, // inspection id
  string, // group id
  PatchGroup,
  string?, // requestKey
];
export const patchGroup = createSmartAction<RequestAction<ApiPatchGroup>, PatchGroupPayload>(
  'difference-groups/patchGroup',
  (inspectionId, groupId, patchData, requestKey) => ({
    request: {
      url: `/inspections/${inspectionId}/difference-groups/${groupId}`,
      method: 'PATCH',
      data: patchData,
    },
    meta: {
      errorMessage: 'There was an error patching the groups. Please try again.',
      requestKey: requestKey || groupId,
      mutations: {
        [fetchGroups.toString()]: (groupsData: FetchGroupsData) => {
          if (groupsData && groupsData[groupId]) {
            return { ...groupsData, [groupId]: { ...groupsData[groupId], ...patchData } };
          }
          return groupsData;
        },
        [fetchDifferences.toString()]: {
          updateData: (
            differencesData: FetchDifferencesData,
            { differenceList: differencesToUpdate, comment }: ApiPatchGroup,
          ): FetchDifferencesData => {
            if (!differencesData) return differencesData;
            return differencesData.map((diff) => {
              if (differencesToUpdate.includes(diff.id)) {
                return {
                  ...diff,
                  comment: comment ?? diff.comment,
                  viewed: true,
                  updatedAt: new Date().toString(),
                };
              }
              return diff;
            });
          },
          updateDataOptimistic(data) {
            return data.map((difference: Difference) => {
              if (difference.groupId === groupId) {
                return {
                  ...difference,
                  ...patchData,
                  viewed: true,
                  updatedAt: new Date().toString(),
                };
              }
              return difference;
            });
          },
        },
      },
    },
  }),
);
